// Thin fetch wrappers against the FastAPI /api/* surface.
//
// Always credentials: "include" so the inbox_session cookie crosses
// origins in prod (web on inbox.ianydifix.de, api on the same host but
// — depending on Caddy config — possibly a sibling origin).
//
// All endpoints throw `ApiError` on non-2xx; consumers wrap in TanStack
// Query so error boundaries / retries are handled centrally.

import { queryClient } from "./queryClient";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function http<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      // CSRF guard: the backend requires this on mutating /api/* requests.
      "X-Requested-With": "inbox",
      ...(init.headers ?? {}),
    },
  });
  if (resp.status === 401) {
    // Session expired/missing: flip the auth cache so <RequireAuth> redirects
    // to /login instead of the page rendering an empty archive.
    queryClient.setQueryData(["auth", "me"], { authenticated: false });
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new ApiError(resp.status, text || resp.statusText);
  }
  // 204 No Content (e.g. DELETE) has no body — don't try to parse it.
  if (resp.status === 204 || resp.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await resp.json()) as T;
}

export interface Capture {
  id: number;
  captured_at: string;
  category: string;
  summary: string;
  body: string;
  tags: string[];
  payload: Record<string, unknown>;
  external_uid: string | null;
  confidence: number;
  source_message_id: number | null;
  // Proactive-delivery scheduling (set for events/reminders with an attachment).
  surface_at?: string | null;
  surfaced_at?: string | null;
  pinned?: number; // 0/1 — floats to the top of the timeline
  archived?: number; // 0/1 — hidden from the main timeline
}

export interface CategoryInfo {
  name: string;
  description: string;
  count: number;
}

// A capture tied to a place (today: place-bound reminders only).
export interface PlaceItem {
  id: number;
  category: string;
  summary: string;
}

// A saved place backing place-bound reminders ("remind me when I'm home").
export interface Place {
  id: number;
  label: string;
  kind: string; // glyph key: home/work/food/… (see KindGlyph)
  lat: number;
  lon: number;
  created_at: string;
  items: PlaceItem[]; // captures tied to this spot (read-only, derived)
}

// The exact phrase that authorizes force-deleting an immutable tax record.
// Mirrors the backend's force_delete_phrase exactly: payload.year (a number, or
// a digit-string from a hand-edited payload), falling back to the capture id.
// Must stay in lockstep with the backend or a record becomes undeletable.
export function taxDeletePhrase(c: Capture): string {
  const year = c.payload?.year;
  const usable =
    typeof year === "number" || (typeof year === "string" && /^\d+$/.test(year));
  return `DELETE TAX ${usable ? year : c.id}`;
}

export const api = {
  whoami: () =>
    http<{ authenticated: boolean; id?: number }>("/api/auth/me"),
  loginWithTelegram: (payload: Record<string, unknown>) =>
    http<{ ok: true; id: number }>("/api/auth/telegram", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  logout: () => http<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  listCaptures: (params: {
    category?: string;
    tag?: string;
    q?: string;
    before_id?: number;
    limit?: number;
    archived?: boolean;
  }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const suffix = qs.toString();
    return http<{ items: Capture[]; next_before_id: number | null }>(
      `/api/captures${suffix ? `?${suffix}` : ""}`,
    );
  },
  getCapture: (id: number) => http<Capture>(`/api/captures/${id}`),
  deleteCapture: (id: number) =>
    http<void>(`/api/captures/${id}`, { method: "DELETE" }),
  // Force-delete an immutable tax record. Gated by a typed confirmation phrase
  // the backend verifies exactly (see taxDeletePhrase).
  forceDeleteCapture: (id: number, confirmation: string) =>
    http<void>(`/api/captures/${id}/force-delete`, {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    }),
  editCapture: (id: number, patch: { title?: string; body?: string; tags?: string[] }) =>
    http<Capture>(`/api/captures/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  // surface_at: ISO string to (re)arm delivery, or null to cancel.
  scheduleCapture: (id: number, surface_at: string | null) =>
    http<Capture>(`/api/captures/${id}/schedule`, {
      method: "POST",
      body: JSON.stringify({ surface_at }),
    }),
  // Mark a reminder/task/shopping capture done (writes COMPLETED to CalDAV).
  completeCapture: (id: number) =>
    http<Capture>(`/api/captures/${id}/complete`, { method: "POST" }),
  // Pin/unpin or archive/unarchive (only the flags provided).
  setFlags: (id: number, flags: { pinned?: boolean; archived?: boolean }) =>
    http<Capture>(`/api/captures/${id}/flags`, {
      method: "POST",
      body: JSON.stringify(flags),
    }),
  listCategories: () =>
    http<{ categories: CategoryInfo[] }>("/api/categories"),

  listPlaces: () => http<{ items: Place[] }>("/api/places"),
  // Add/replace a place: explicit coords (map pin) or an address (geocoded).
  addPlace: (body: {
    label: string;
    kind?: string;
    lat?: number;
    lon?: number;
    address?: string;
  }) => http<Place>("/api/places", { method: "POST", body: JSON.stringify(body) }),
  deletePlace: (id: number) =>
    http<{ deleted: Place }>(`/api/places/${id}`, { method: "DELETE" }),
  // Preview a typed address → coords (drops a pin before saving). 404 = no match.
  geocodePreview: (q: string) =>
    http<{ lat: number; lon: number; display: string }>(
      `/api/places/geocode?q=${encodeURIComponent(q)}`,
    ),

  ask: (question: string) =>
    http<{
      answer: string;
      sources: { id: number; summary: string; category: string }[];
    }>("/api/ask", {
      method: "POST",
      body: JSON.stringify({ question }),
    }),
};
